import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import LanRoundedIcon from '@mui/icons-material/LanRounded';
import MonitorRoundedIcon from '@mui/icons-material/MonitorRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SyncDisabledRoundedIcon from '@mui/icons-material/SyncDisabledRounded';
import SyncProblemRoundedIcon from '@mui/icons-material/SyncProblemRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  Typography,
  Badge,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { keyframes } from '@emotion/react';
import AlarmPopperMenu from './AlarmPopperMenu';

const StatusChip = ({ status }) => {
  const { t } = useTranslation('energyxplorer');
  const label = t(`app_bar.status.${status}`);
  switch (status) {
    case 'Synced':
      return (
        <Tooltip title={label} placement="top">
          <IconButton disableRipple sx={{ cursor: 'default' }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <CheckCircleRoundedIcon
                  sx={{
                    fontSize: 14,
                    color: 'success.main',
                    bgcolor: 'background.paper',
                    borderRadius: '50%',
                  }}
                />
              }
            >
              <SyncRoundedIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      );
    case 'Desynced':
      return (
        <Tooltip title={label} placement="top">
          <IconButton disableRipple sx={{ cursor: 'default' }}>
            <SyncProblemRoundedIcon />
          </IconButton>
        </Tooltip>
      );
    case 'Processing':
      return (
        <Tooltip title={label} placement="top">
          <IconButton disableRipple sx={{ cursor: 'default' }}>
            <SyncRoundedIcon
              color="info"
              sx={{
                animation: 'spinReverse 2s linear infinite',
                '@keyframes spinReverse': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(-360deg)' }, // negative = counter-clockwise
                },
              }}
            />
          </IconButton>
        </Tooltip>
      );
    default:
      return (
        <Tooltip title={label} placement="top">
          <IconButton disableRipple sx={{ cursor: 'default' }}>
            <SyncDisabledRoundedIcon />
          </IconButton>
        </Tooltip>
      );
  }
};

// Define the animation
const pulseBorder = keyframes`
  0% {
    box-shadow: 0 0 0 0 #4CAF50;
  }
  70% {
    box-shadow: 0 0 0 6px rgba(0, 255, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 255, 0, 0);
  }
`;

export default function AppBarMenu({
  setValue,
  value,
  selectedEnergyGroup,
  handleAlarmClick,
  handleEnergyGroupCreate,
  handleEnergyGroupSettingsClick,
  handlePrintClick,
  setDeleteEnergyGroupDialogOpen,
  alarms,
  energyGroupSelector,
  dateRangeComponent,
}) {
  const { t } = useTranslation('energyxplorer', 'common');
  const [openMenuIndex, setOpenMenuIndex] = useState(null);

  const anchorRefs = useRef([]);

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const shouldPulse = (label) =>
    label === 'Opsætning' || label === 'Opret ny'
      ? !selectedEnergyGroup
      : false;

  const menuItems = [
    {
      label: t('app_bar.consumption_overview'),
      icon: <MonitorRoundedIcon />,
      value: 0,
      onClick: () => setValue(0),
    },
    {
      label: t('app_bar.energy_breakdown'),
      value: 1,
      icon: <LanRoundedIcon />,
      onClick: () => setValue(1),
      submenu: [
        {
          label: t('app_bar.energy_tree'),
          value: 1,
          onClick: () => setValue(1),
        },
        {
          label: t('app_bar.energy_tree_overview'),
          value: 26,
          onClick: () => setValue(26),
        },
      ],
    },
    {
      label: t('app_bar.energy_analysis'),
      icon: <QueryStatsRoundedIcon />,
      value: 2,
      onClick: () => setValue(25),
      submenu: [
        {
          label: t('app_bar.energy_analysis_breakdown'),
          value: 25,
          onClick: () => setValue(25),
        },
        {
          label: t('app_bar.energy_analysis_hour'),
          value: 24,
          onClick: () => setValue(24),
        },
        {
          label: t('app_bar.energy_analysis_duration'),
          value: 23,
          onClick: () => setValue(23),
        },
        {
          label: t('app_bar.energy_analysis_node'),
          value: 2,
          onClick: () => setValue(2),
        },
        {
          label: t('app_bar.energy_analysis_compare'),
          value: 21,
          onClick: () => setValue(21),
        },
        {
          label: t('app_bar.energy_analysis_load_profile'),
          value: 27,
          onClick: () => setValue(27),
        },
      ],
    },
    {
      label: t('app_bar.initiatives'),
      icon: <EnergySavingsLeafRoundedIcon />,
      value: 3,
      onClick: () => setValue(3),
    },
    {
      label: t('app_bar.esg'),
      icon: (
        <img
          width="23"
          height="23"
          src="https://img.icons8.com/deco-glyph/48/hand-planting.png"
          alt="hand-planting"
          style={{
            filter:
              value === 4
                ? isDarkMode
                  ? 'invert(100%)'
                  : 'invert(0%)'
                : isDarkMode
                  ? 'invert(65%)'
                  : 'invert(30%)',
          }}
        />
      ),
      value: 4,
      onClick: () => setValue(4),
    },
  ];

  const handleClose = (event, index) => {
    if (anchorRefs.current[index]?.contains(event.target)) return;
    setOpenMenuIndex(null);
  };

  const hoverTimers = useRef({}); // index -> timeout ID

  const handleMouseEnter = (index) => {
    clearTimeout(hoverTimers.current[index]);
    hoverTimers.current[index] = setTimeout(() => {
      setOpenMenuIndex(index);
    }, 300);
  };

  const handleMouseLeave = (index) => {
    clearTimeout(hoverTimers.current[index]);
    hoverTimers.current[index] = setTimeout(() => {
      setOpenMenuIndex(null);
    }, 200); // optionally delay closing
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        px: 1,
        py: 1,
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {menuItems.map((item, index) => {
          const hasMenu = Boolean(item.submenu);
          const open = openMenuIndex === index;
          const isSelected =
            item.value === value ||
            (item.submenu &&
              item.submenu.some((subItem) => subItem.value === value));

          return (
            <Box key={item.label} sx={{ position: 'relative' }}>
              <Button
                ref={(el) => (anchorRefs.current[index] = el)}
                onClick={() => item.onClick()}
                onMouseEnter={() => hasMenu && handleMouseEnter(index)}
                onMouseLeave={() => hasMenu && handleMouseLeave(index)}
                startIcon={item.icon || null}
                endIcon={
                  hasMenu ? (
                    <Box
                      component="span"
                      sx={{
                        display: 'flex',
                        transition: 'transform 0.2s ease-in-out',
                        transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                      }}
                    >
                      <KeyboardArrowRightRoundedIcon fontSize="small" />
                    </Box>
                  ) : null
                }
                sx={{
                  textTransform: 'none',
                  color: isSelected ? 'text.primary' : 'text.secondary',
                  fontSize: '0.9rem',
                  boxShadow: shouldPulse(item.label)
                    ? '0 0 0 0 #4CAF50'
                    : 'none',
                  animation: shouldPulse(item.label)
                    ? `${pulseBorder} 2s infinite`
                    : 'none',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: isSelected ? 'action.hover' : 'transparent',
                  '&:hover': {
                    bgcolor: isSelected ? 'action.hover' : 'action.hover',
                    color: 'text.primary',
                  },
                }}
              >
                {item.label === 'Analyse' &&
                item.submenu?.some((sub) => sub.value === value)
                  ? item.submenu.find((sub) => sub.value === value)?.label
                  : item.label}
              </Button>

              {hasMenu && (
                <Popper
                  open={open}
                  anchorEl={anchorRefs.current[index]}
                  placement="bottom-end"
                  transition
                  disablePortal
                  sx={{ zIndex: 1301 }}
                  onMouseEnter={() => handleMouseEnter(index)}
                  onMouseLeave={() => handleMouseLeave(index)}
                >
                  {({ TransitionProps }) => (
                    <Grow
                      {...TransitionProps}
                      style={{ transformOrigin: 'left top' }}
                    >
                      <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{ mt: 0.5, borderRadius: 2 }}
                      >
                        <ClickAwayListener
                          onClickAway={(event) => handleClose(event, index)}
                        >
                          <MenuList
                            autoFocusItem={open}
                            dense
                            sx={{ minWidth: 180, p: 1 }}
                          >
                            {item.submenu.map((subItem, index) => {
                              const isSubSelected = subItem.value === value;

                              return (
                                <MenuItem
                                  key={index}
                                  onClick={() => {
                                    setOpenMenuIndex(null); // Close the popper
                                    subItem.onClick(); // Call the submenu's action
                                  }}
                                  sx={{
                                    py: '6px',
                                    px: '12px',
                                    boxShadow: shouldPulse(subItem.label)
                                      ? '0 0 0 0 #4CAF50'
                                      : 'none',
                                    animation: shouldPulse(subItem.label)
                                      ? `${pulseBorder} 2s infinite`
                                      : 'none',
                                    borderRadius: '5px',
                                    bgcolor: isSubSelected
                                      ? 'action.hover'
                                      : 'transparent',
                                    '&:hover': {
                                      // bgcolor: isSubSelected ? 'hsla(67, 100%, 40%, 0.2)' : 'action.hover',
                                      bgcolor: isSubSelected
                                        ? 'action.hover'
                                        : 'action.hover',
                                      transition: 'all 0.3s ease',
                                      color: subItem.label.includes('Fjern')
                                        ? 'red'
                                        : 'inherit',
                                    },
                                  }}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    {subItem.icon && subItem.icon}
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                    >
                                      {subItem.label}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              )}
            </Box>
          );
        })}
      </Box>
      <Box
        sx={{
          display: 'flex',
          marginLeft: 'auto',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {/*         <AlarmPopperMenu
          alarms={alarms || []}
          handleAlarmClick={handleAlarmClick}
        /> */}
        <IconButton
          ref={(el) => (anchorRefs.current[menuItems.length + 1] = el)}
          onMouseEnter={() => handleMouseEnter(menuItems.length + 1)}
          onMouseLeave={() => handleMouseLeave(menuItems.length + 1)}
          onClick={() => handlePrintClick('all')}
          sx={{
            textTransform: 'none',
            // fontSize: '0.9rem',
            // px: 2,
            py: 1,
            borderRadius: 2,
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'action.hover',
              color: 'text.primary',
            },
          }}
        >
          <PrintRoundedIcon />
        </IconButton>

        <Popper
          open={openMenuIndex === menuItems.length + 1}
          anchorEl={anchorRefs.current[menuItems.length + 1]}
          placement="bottom-start"
          transition
          disablePortal
          sx={{ zIndex: 1301 }}
          onMouseEnter={() => handleMouseEnter(menuItems.length + 1)}
          onMouseLeave={() => handleMouseLeave(menuItems.length + 1)}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: 'left top' }}>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{ mt: 0.5, borderRadius: 2 }}
              >
                <ClickAwayListener
                  onClickAway={(event) =>
                    handleClose(event, menuItems.length + 1)
                  }
                >
                  <MenuList dense sx={{ minWidth: 180, p: 1 }}>
                    <MenuItem onClick={() => handlePrintClick(value)}>
                      <Box
                        component="span"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography variant="body2" fontWeight="medium">
                          {t('app_bar.print_this')}
                        </Typography>
                      </Box>
                    </MenuItem>
                    {selectedEnergyGroup && (
                      <>
                        <MenuItem onClick={() => handlePrintClick('all')}>
                          <Box
                            component="span"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2" fontWeight="medium">
                              {t('app_bar.print_all')}
                            </Typography>
                          </Box>
                        </MenuItem>
                      </>
                    )}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
        <Divider orientation="vertical" ml={2} />
        <StatusChip status={selectedEnergyGroup?.options.state || 'Unknown'} />
        <IconButton
          ref={(el) => (anchorRefs.current[menuItems.length] = el)}
          onMouseEnter={() => handleMouseEnter(menuItems.length)}
          onMouseLeave={() => handleMouseLeave(menuItems.length)}
          sx={{
            textTransform: 'none',
            // fontSize: '0.9rem',
            // px: 2,
            py: 1,
            borderRadius: 2,
            color: 'text.secondary',
            animation: !selectedEnergyGroup
              ? `${pulseBorder} 2s infinite`
              : 'none',
            boxShadow: !selectedEnergyGroup ? '0 0 0 0 #4CAF50' : 'none',
            '&:hover': {
              bgcolor: 'action.hover',
              color: 'text.primary',
            },
          }}
        >
          <SettingsRoundedIcon />
        </IconButton>

        <Popper
          open={openMenuIndex === menuItems.length}
          anchorEl={anchorRefs.current[menuItems.length]}
          placement="bottom-end"
          transition
          disablePortal
          sx={{ zIndex: 1301 }}
          onMouseEnter={() => handleMouseEnter(menuItems.length)}
          onMouseLeave={() => handleMouseLeave(menuItems.length)}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: 'left top' }}>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{ mt: 0.5, borderRadius: 2 }}
              >
                <ClickAwayListener
                  onClickAway={(event) => handleClose(event, menuItems.length)}
                >
                  <MenuList dense sx={{ minWidth: 180, p: 1 }}>
                    <MenuItem onClick={handleEnergyGroupCreate}>
                      <Box
                        component="span"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <AddRoundedIcon />
                        <Typography variant="body2" fontWeight="medium">
                          {t('energy_group_settings.add_energy_group')}
                        </Typography>
                      </Box>
                    </MenuItem>
                    {selectedEnergyGroup && (
                      <>
                        <MenuItem onClick={handleEnergyGroupSettingsClick}>
                          <Box
                            component="span"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <EditRoundedIcon />
                            <Typography variant="body2" fontWeight="medium">
                              {t('energy_group_settings.edit_energy_group')}{' '}
                              {selectedEnergyGroup.name}
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem
                          onClick={() => setDeleteEnergyGroupDialogOpen(true)}
                          sx={{
                            '&:hover': {
                              color: 'red',
                            },
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <DeleteRoundedIcon />
                            <Typography variant="body2" fontWeight="medium">
                              {t('energy_group_settings.delete_energy_group')}{' '}
                              {selectedEnergyGroup.name}
                            </Typography>
                          </Box>
                        </MenuItem>
                      </>
                    )}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>

        <Tooltip
          title={t('energy_group_select.select_energy_group')}
          enterDelay={500}
          placement="top"
        >
          {energyGroupSelector}
        </Tooltip>
        {dateRangeComponent}
      </Box>
    </Paper>
  );
}
